import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { MetadataKind } from '../../metadata-mgmt/models/Metadata-mgmt';
import {
  ICrossFieldRule,
  ICrossFieldRuleCreate,
  ICrossFieldRulePatch,
  IRuleFilters,
  IRuleOperation,
  IRuleParamDefinitionCreate,
  IRuleParamDefinitionPatch,
  IRuleTypeCatalogEntry,
  IRuleTypeDefinition,
  IRuleTypeDefinitionPayload,
  IValidatorRule,
  IValidatorRuleCreate,
  IValidatorRulePatch,
} from '../models/validation-rule';
import { ValidationRulesUiActions } from '../state/actions';
import * as selectors from '../state/selectors/validation-rules.selectors';

@Injectable({ providedIn: 'root' })
export class ValidationRulesFacade {
  private store = inject(Store);

  // Observable + Signal pairs follow `DomainAllowlistFacade`'s pattern so
  // components can choose whichever style suits them. New code in this
  // module uses signals consistently.
  fieldRules$ = this.store.select(selectors.selectFieldRules);
  crossFieldRules$ = this.store.select(selectors.selectCrossFieldRules);
  catalog$ = this.store.select(selectors.selectCatalog);
  loading$ = this.store.select(selectors.selectLoading);
  selectedSourceTypeId$ = this.store.select(selectors.selectSelectedSourceTypeId);
  selectedTemplateId$ = this.store.select(selectors.selectSelectedTemplateId);
  selectedKind$ = this.store.select(selectors.selectSelectedKind);
  fieldRulesForSelectedTemplate$ = this.store.select(selectors.selectFieldRulesForSelectedTemplate);
  crossFieldRulesForSelectedTemplate$ = this.store.select(
    selectors.selectCrossFieldRulesForSelectedTemplate,
  );
  valueDependencyRules$ = this.store.select(selectors.selectValueDependencyRules);

  fieldRulesSignal: Signal<IValidatorRule[]> = toSignal(this.fieldRules$, { initialValue: [] });
  crossFieldRulesSignal: Signal<ICrossFieldRule[]> = toSignal(this.crossFieldRules$, {
    initialValue: [],
  });
  catalogSignal: Signal<IRuleTypeCatalogEntry[]> = toSignal(this.catalog$, { initialValue: [] });
  selectedSourceTypeIdSignal: Signal<number | null> = toSignal(this.selectedSourceTypeId$, {
    initialValue: null,
  });
  selectedTemplateIdSignal: Signal<number | null> = toSignal(this.selectedTemplateId$, {
    initialValue: null,
  });
  selectedKindSignal: Signal<MetadataKind> = toSignal(this.selectedKind$, {
    initialValue: 'BASELINE',
  });
  fieldRulesForSelectedTemplateSignal: Signal<IValidatorRule[]> = toSignal(
    this.fieldRulesForSelectedTemplate$,
    { initialValue: [] },
  );
  crossFieldRulesForSelectedTemplateSignal: Signal<ICrossFieldRule[]> = toSignal(
    this.crossFieldRulesForSelectedTemplate$,
    { initialValue: [] },
  );
  valueDependencyRulesSignal: Signal<ICrossFieldRule[]> = toSignal(this.valueDependencyRules$, {
    initialValue: [],
  });

  // Configurable catalog
  operations$ = this.store.select(selectors.selectOperations);
  ruleTypeDefs$ = this.store.select(selectors.selectRuleTypeDefs);
  operationsSignal: Signal<IRuleOperation[]> = toSignal(this.operations$, { initialValue: [] });
  ruleTypeDefsSignal: Signal<IRuleTypeDefinition[]> = toSignal(this.ruleTypeDefs$, {
    initialValue: [],
  });

  // ---- dispatch helpers -------------------------------------------

  loadFieldRules(filters: IRuleFilters = {}): void {
    this.store.dispatch(ValidationRulesUiActions.LoadFieldRules({ filters }));
  }

  loadCrossFieldRules(filters: IRuleFilters = {}): void {
    this.store.dispatch(ValidationRulesUiActions.LoadCrossFieldRules({ filters }));
  }

  loadCatalog(): void {
    this.store.dispatch(ValidationRulesUiActions.LoadCatalog());
  }

  createFieldRule(payload: IValidatorRuleCreate): void {
    this.store.dispatch(ValidationRulesUiActions.CreateFieldRule({ payload }));
  }

  patchFieldRule(id: number, changes: IValidatorRulePatch): void {
    this.store.dispatch(ValidationRulesUiActions.PatchFieldRule({ id, changes }));
  }

  deleteFieldRule(id: number): void {
    this.store.dispatch(ValidationRulesUiActions.DeleteFieldRule({ id }));
  }

  createCrossFieldRule(payload: ICrossFieldRuleCreate): void {
    this.store.dispatch(ValidationRulesUiActions.CreateCrossFieldRule({ payload }));
  }

  patchCrossFieldRule(id: number, changes: ICrossFieldRulePatch): void {
    this.store.dispatch(ValidationRulesUiActions.PatchCrossFieldRule({ id, changes }));
  }

  deleteCrossFieldRule(id: number): void {
    this.store.dispatch(ValidationRulesUiActions.DeleteCrossFieldRule({ id }));
  }

  selectSourceType(sourceTypeId: number | null): void {
    this.store.dispatch(ValidationRulesUiActions.SelectSourceType({ sourceTypeId }));
  }

  selectTemplate(templateId: number | null): void {
    this.store.dispatch(ValidationRulesUiActions.SelectTemplate({ templateId }));
  }

  selectKind(kind: MetadataKind): void {
    this.store.dispatch(ValidationRulesUiActions.SelectKind({ kind }));
  }

  // ---- configurable catalog ----------------------------------------

  loadOperations(): void {
    this.store.dispatch(ValidationRulesUiActions.LoadOperations());
  }

  loadRuleTypeDefinitions(): void {
    this.store.dispatch(ValidationRulesUiActions.LoadRuleTypeDefinitions());
  }

  createRuleTypeDefinition(payload: IRuleTypeDefinitionPayload & { code: string }): void {
    this.store.dispatch(ValidationRulesUiActions.CreateRuleTypeDefinition({ payload }));
  }

  patchRuleTypeDefinition(id: number, changes: Partial<IRuleTypeDefinitionPayload>): void {
    this.store.dispatch(ValidationRulesUiActions.PatchRuleTypeDefinition({ id, changes }));
  }

  deleteRuleTypeDefinition(id: number): void {
    this.store.dispatch(ValidationRulesUiActions.DeleteRuleTypeDefinition({ id }));
  }

  createParamDefinition(payload: IRuleParamDefinitionCreate): void {
    this.store.dispatch(ValidationRulesUiActions.CreateParamDefinition({ payload }));
  }

  patchParamDefinition(id: number, changes: IRuleParamDefinitionPatch): void {
    this.store.dispatch(ValidationRulesUiActions.PatchParamDefinition({ id, changes }));
  }

  deleteParamDefinition(id: number): void {
    this.store.dispatch(ValidationRulesUiActions.DeleteParamDefinition({ id }));
  }
}
